import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
    ClockIcon,
    CurrencyRupeeIcon,
    CheckBadgeIcon,
    ArrowRightIcon,
    AcademicCapIcon
} from '@heroicons/react/24/outline'

const FeaturedCourses = ({ courses = [] }) => {
    if (courses.length === 0) {
        return null
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: 'easeOut',
            },
        },
    }

    const getSpecializationColor = (specialization) => {
        const colors = {
            'Computer Science': 'bg-accent-100 text-accent-800',
            'Engineering': 'bg-secondary-100 text-secondary-800',
            'Business': 'bg-primary-100 text-primary-800',
            'Science': 'bg-gold-100 text-gold-800',
            'Arts': 'bg-accent-100 text-accent-800',
            'Law': 'bg-primary-100 text-primary-800',
            'Medical': 'bg-secondary-100 text-secondary-800',
            'Other': 'bg-gray-100 text-gray-800',
        }
        return colors[specialization] || colors['Other']
    }

    const getLevelColor = (level) => {
        const colors = {
            'Bachelor': 'bg-secondary-100 text-secondary-800',
            'Master': 'bg-primary-100 text-primary-800',
            'PhD': 'bg-accent-100 text-accent-800',
            'Diploma': 'bg-gold-100 text-gold-800',
            'Certificate': 'bg-gray-100 text-gray-800',
        }
        return colors[level] || colors['Certificate']
    }



    return (
        <section className="py-16 lg:py-24 bg-gray-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 lg:mb-16"
                >
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                        Popular Courses
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Discover our most sought-after programs designed to accelerate your career growth
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                >
                    {courses.map((course, index) => (
                        <motion.div
                            key={course._id || course.id}
                            variants={itemVariants}
                            className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col h-full group"
                        >
                            {/* Header - Clickable Title */}
                            <div className="mb-4">
                                <Link to={`/courses/${course.slug}`} className="block">
                                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2 min-h-[3.5rem]" title={course.degreeId ? course.degreeId.name : course.name}>
                                        {course.degreeId ? course.degreeId.name : `${course.level} - ${course.specialization ? course.specialization.join(', ') : 'Course'}`}
                                    </h3>
                                </Link>
                            </div>

                            {/* Tags/Badges - Fixed height container for consistency */}
                            <div className="flex flex-wrap gap-2 mb-6 min-h-[3rem] content-start">
                                {/* Level Badge */}
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getLevelColor(course.level)}`}>
                                    {course.level}
                                </span>

                                {/* Specialization Badges */}
                                {course.specialization && course.specialization.length > 0 && (
                                    <>
                                        {course.specialization.slice(0, 2).map((spec, i) => {
                                            const isObjectId = /^[0-9a-fA-F]{24}$/.test(spec);
                                            if (isObjectId) return null;
                                            return (
                                                <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                                                    {spec}
                                                </span>
                                            )
                                        })}
                                        {course.specialization.length > 2 && (
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                                +{course.specialization.length - 2}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Course Details - Info Rows */}
                            <div className="space-y-3 mb-6 flex-1">
                                <div className="flex items-center text-gray-600">
                                    <ClockIcon className="w-5 h-5 mr-3 text-gray-400 shrink-0" />
                                    <span className="text-sm font-medium">{course.duration || 'Duration N/A'}</span>
                                </div>
                                <div className="flex items-center text-gray-600">
                                    <CurrencyRupeeIcon className="w-5 h-5 mr-3 text-gray-400 shrink-0" />
                                    <span className="text-sm font-medium">
                                        {course.universityId?.fee_range?.type === 'below_2_lakhs' ? 'Below ₹2 Lakhs' :
                                            course.universityId?.fee_range?.type === 'above_2_lakhs' ? 'Above ₹2 Lakhs' :
                                                'Fee Range Available'}
                                    </span>
                                </div>
                            </div>

                            {/* Action Button - Always at bottom */}
                            <div className="mt-auto">
                                <Link
                                    to={`/courses/${course.slug}`}
                                    className="btn btn-outline btn-lg inline-flex items-center space-x-2 group"
                                >
                                    View Details
                                </Link>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* View All Button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-center mt-12"
                >
                    <Link
                        to="/courses"
                        className="btn-outline btn-lg inline-flex items-center space-x-2 group"
                    >
                        <span>Explore All Courses</span>
                        <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </motion.div>
            </div>
        </section>
    )
}

export default FeaturedCourses
